import React from "react";
import { NoteType } from "@/components/note/types";
import { Maybe } from "@/domains/common/types";
import {
  MDSCollectionIcon,
  MDSIconKind,
  MDSMenuItemProps,
  MDSMenuItemVariant,
  MDSShortcutKey,
  MDSShortcutKeyCharacter,
  MDSShortcutKeyGroup,
} from "@/mds";
import type { AppStore } from "@/store";
import uFuzzy from "@leeoniya/ufuzzy";
import {
  MemCommonEditorAction,
  MemCommonEditorActionKind,
  MemCommonEditorFileVariant,
  MemCommonEditorSlashCommand,
  MemCommonMentionKind,
} from "@mem-labs/common-editor";
import { DateTime } from "luxon";
import { fileModule } from "@/modules/file";
import { fileModule as commonEditorFileModule } from "@mem-labs/common-editor";
import { v4 as uuidv4 } from "uuid";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import { SearchSuggestion, SearchSuggestionType } from "@/domains/search";
import { UNTITLED_NOTE_TITLE, UNTITLED_TEMPLATE_TITLE } from "@/domains/untitled/untitled";
import { PaywallContext, TrackedEvent, TrackedEventContext, trackEvent } from "@/domains/metrics";
import { TemplateVariableCode } from "@/store/templates/types";
import { NoteObservable } from "@/store/note";
import { generateRecentDateString } from "@/domains/date/date";
import { MENTION_PREFIX_COLLECTION, MENTION_PREFIX_NOTE } from "@/domains/mentions/constants";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { EventContext } from "@/domains/metrics/context";
import { notesModule } from "@/modules/notes";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";

enum InsertFileMode {
  Attachment,
  Image,
}

export interface MemCommonEditorMentionChip {
  id: string;
  kind: MemCommonMentionKind;
  searchTerms: string[];
  menuItem: MDSMenuItemProps;
  beforeSelection?: () => Promise<void>;
  alwaysVisible?: boolean;
  groupTitle?: string;
  action?: MemCommonEditorSlashCommand | (() => Promise<Maybe<MemCommonEditorSlashCommand>>);
  featuredIndex?: number;
  metadata?: string;
}

const NOTES_MAX_RESULTS = 100;
const COLLECTIONS_MAX_RESULTS = 5;
const SLASH_MENU_MAX_SEARCH_RESULTS_PER_GROUP = 3;
const SLASH_MENU_GROUP_TITLES = {
  Date: "Insert date",
  Attachment: "Insert attachment",
  Formatting: "Insert formatting",
  Template: "Insert template",
  CalendarEventTemplate: "Insert meeting template variable",
  TemplateVariable: "Insert template variable",
  RecentNotes: "Mention a recent note",
  Notes: "Mention a note",
  RecentCollections: "Mention a recent collection",
  Collections: "Mention a collection",
};

export class MemCommonEditorMentions {
  store: AppStore;
  dispatchAction: (event: MemCommonEditorAction) => void;
  fuzzySearcher: uFuzzy;

  constructor({
    store,
    dispatchAction,
  }: {
    store: AppStore;
    dispatchAction: (event: MemCommonEditorAction) => void;
  }) {
    this.store = store;
    this.dispatchAction = dispatchAction;
    this.fuzzySearcher = new uFuzzy();
  }

  private findChipMatches(
    query: string,
    chips: MemCommonEditorMentionChip[]
  ): MemCommonEditorMentionChip[] {
    const haystack = chips.map(chip => chip.searchTerms.join(" "));
    const indexes = this.fuzzySearcher.filter(haystack, query);
    return indexes?.map(index => chips[index]) || [];
  }

  private search = async ({
    mentionQueryText,
    excludeId,
    suggestionType,
  }: {
    mentionQueryText: string;
    excludeId?: string;
    suggestionType?: SearchSuggestionType;
  }): Promise<SearchSuggestion[]> => {
    const andCondition = (item: SearchSuggestion) => {
      let output = true;
      if (suggestionType) output = output && item.type === suggestionType;
      if (excludeId) output = output && item.modelId !== excludeId;
      return output;
    };
    const suggestions = await this.store.search.forSuggestions(
      mentionQueryText,
      "mentions",
      andCondition,
      1000
    );

    return suggestions;
  };

  getAvailableChips = async (
    mentionQuery: string,
    opts: {
      inlineCreation?: boolean;
      excludeId?: string;
      noteType: NoteType;
      isCalendarEventTemplate?: boolean;
    }
  ): Promise<MemCommonEditorMentionChip[]> => {
    const mentionChar = mentionQuery[0];
    const mentionQueryText = mentionQuery.slice(1).trim();

    if (mentionChar === "/") {
      return this.getSlashCommandChips({
        mentionQueryText,
        noteType: opts.noteType,
        isCalendarEventTemplate: opts.isCalendarEventTemplate,
      });
    }

    if (mentionChar === MENTION_PREFIX_NOTE) {
      return this.getNoteSuggestionChips({
        mentionQueryText,
        excludeId: opts.excludeId,
        inlineCreation: opts.inlineCreation,
      });
    }

    if (mentionChar === MENTION_PREFIX_COLLECTION) {
      return this.getCollectionSuggestionChips({
        mentionQueryText,
        excludeId: opts.excludeId,
        inlineCreation: opts.inlineCreation,
      });
    }

    return [];
  };

  getSlashCommandChips = async ({
    mentionQueryText,
    noteType,
    isCalendarEventTemplate,
  }: {
    mentionQueryText: string;
    noteType: NoteType;
    isCalendarEventTemplate?: boolean;
  }) => {
    const availableChips: MemCommonEditorMentionChip[] = [];

    if (noteType === NoteType.Template) {
      const templateVariableChips = await this.getTemplateVariableChips({
        isCalendarEventTemplate,
      });
      availableChips.push(...templateVariableChips);
    }

    if (noteType === NoteType.Note) {
      const templateSuggestionChips = await this.getTemplateSuggestionChips({ mentionQueryText });
      availableChips.push(...templateSuggestionChips);
    }

    const baseSlashCommandChips = await this.getBaseSlashCommandChips();
    availableChips.push(...baseSlashCommandChips);

    // If there is no query, return the base case
    if (!mentionQueryText) return availableChips;

    // If there is a query, find matching chips using fuzzy search and trim to three results per group
    const suggestedChips = this.findChipMatches(mentionQueryText, availableChips);
    const groupedChips = new Map<string | undefined, MemCommonEditorMentionChip[]>();

    for (const chip of suggestedChips) {
      const group = chip.groupTitle;
      if (!groupedChips.has(group)) groupedChips.set(group, []);
      groupedChips.get(group)?.push(chip);
    }

    const trimmedChips: MemCommonEditorMentionChip[] = [];
    for (const group of groupedChips.values()) {
      trimmedChips.push(...group.slice(0, SLASH_MENU_MAX_SEARCH_RESULTS_PER_GROUP));
    }

    return trimmedChips;
  };

  getBaseSlashCommandChips = async (): Promise<MemCommonEditorMentionChip[]> => {
    const today = DateTime.now().toLocaleString(DateTime.DATE_FULL);
    const yesterday = DateTime.now().minus({ days: 1 }).toLocaleString(DateTime.DATE_FULL);
    const tomorrow = DateTime.now().plus({ days: 1 }).toLocaleString(DateTime.DATE_FULL);
    return [
      // INSERT DATE
      {
        id: "insert-date-today",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["date", "day", "today"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Date,
        action: {
          kind: MemCommonEditorActionKind.InsertText,
          payload: { text: today },
        },
        menuItem: {
          id: "insert-date-today",
          label: "Today",
          extraText: today,
          icon: MDSIconKind.CalendarDay,
        },
      },
      {
        id: "insert-date-yesterday",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["date", "day", "yesterday", yesterday],
        groupTitle: SLASH_MENU_GROUP_TITLES.Date,
        action: {
          kind: MemCommonEditorActionKind.InsertText,
          payload: { text: yesterday },
        },
        menuItem: {
          id: "insert-date-yesterday",
          label: "Yesterday",
          extraText: yesterday,
          icon: MDSIconKind.CalendarDay,
        },
      },
      {
        id: "insert-date-tomorrow",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["date", "day", "tomorrow", tomorrow],
        groupTitle: SLASH_MENU_GROUP_TITLES.Date,
        action: {
          kind: MemCommonEditorActionKind.InsertText,
          payload: { text: tomorrow },
        },
        menuItem: {
          id: "insert-date-tomorrow",
          label: "Tomorrow",
          extraText: tomorrow,
          icon: MDSIconKind.CalendarDay,
        },
      },

      // INSERT ATTACHMENT
      {
        id: "insert-image",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["image", "photo", "picture", "png", "jpg", "jpeg"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Attachment,
        action: () => this.insertFileAction(InsertFileMode.Image),
        menuItem: {
          id: "insert-image",
          label: "Image",
          icon: MDSIconKind.Image,
        },
      },
      {
        id: "insert-file",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["file", "pdf", "zip", "csv", "mp3", "mp4", "mov", "doc", "xls"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Attachment,
        action: () => this.insertFileAction(InsertFileMode.Attachment),
        menuItem: {
          id: "insert-file",
          label: "File",
          icon: MDSIconKind.Paperclip,
        },
      },

      // FORMATTING
      {
        id: "insert-header-1",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["large", "header", "section", "title", "h1"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleHeaderTextFormat,
          payload: {
            level: 1,
            focus: true,
          },
        },
        menuItem: {
          id: "insert-header-1",
          label: "Large header",
          icon: MDSIconKind.H1,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Hashtag} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-header-2",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["medium", "header", "section", "subtitle", "h2"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleHeaderTextFormat,
          payload: {
            level: 2,
            focus: true,
          },
        },
        menuItem: {
          id: "insert-header-2",
          label: "Medium header",
          icon: MDSIconKind.H2,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Hashtag} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Hashtag} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-header-3",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["small", "header", "section", "h3"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleHeaderTextFormat,
          payload: {
            level: 3,
            focus: true,
          },
        },
        menuItem: {
          id: "insert-header-3",
          label: "Small header",
          icon: MDSIconKind.H3,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Hashtag} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Hashtag} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Hashtag} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-check-list",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["task", "list", "checklist", "todos", "checkbox", "action", "item"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleChecklistTextFormat,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-check-list",
          label: "Task list",
          icon: MDSIconKind.Tasks,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.SquareBracketOpen} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.SquareBracketClose} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-bullet-list",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["bulleted", "list", "unordered"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleBulletListTextFormat,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-bullet-list",
          label: "Bulleted list",
          icon: MDSIconKind.List,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Dash} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-ordered-list",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["numbered", "list", "ordered"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleOrderedListTextFormat,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-ordered-list",
          label: "Numbered list",
          icon: MDSIconKind.NumberedList,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.One} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Period} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-table",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["table", "sheet", "column", "row"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.InsertTable,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-table",
          label: "Table",
          icon: MDSIconKind.Table,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Pipe} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-inline-code",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["inline", "code", "script", "javascript", "function", "css", "html"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleInlineCodeTextFormat,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-inline-code",
          label: "Code",
          icon: MDSIconKind.Code,
          rightAddon: () => <MDSShortcutKey character={MDSShortcutKeyCharacter.Backtick} />,
        },
      },
      {
        id: "insert-codeblock",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["code", "block", "script", "javascript", "function", "css", "html"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleCodeBlockTextFormat,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-codeblock",
          label: "Code block",
          icon: MDSIconKind.CodeBlock,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Backtick} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Backtick} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Backtick} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Enter} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-blockquote",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["quote", "blockquote", "quotation"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.ToggleQuoteBlockTextFormat,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-blockquote",
          label: "Quote",
          icon: MDSIconKind.QuoteRight,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.GreaterThan} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Space} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
      {
        id: "insert-divider",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["hr", "divider", "separator", "line", "rule", "break"],
        groupTitle: SLASH_MENU_GROUP_TITLES.Formatting,
        action: {
          kind: MemCommonEditorActionKind.InsertHorizontalRule,
          payload: { focus: true },
        },
        menuItem: {
          id: "insert-divider",
          label: "Divider",
          icon: MDSIconKind.HorizontalRule,
          rightAddon: () => (
            <MDSShortcutKeyGroup>
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Dash} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Dash} />
              <MDSShortcutKey character={MDSShortcutKeyCharacter.Dash} />
            </MDSShortcutKeyGroup>
          ),
        },
      },
    ];
  };

  getTemplateVariableChips = async ({
    isCalendarEventTemplate,
  }: {
    isCalendarEventTemplate?: boolean;
  }): Promise<MemCommonEditorMentionChip[]> => {
    const variables = this.store.templates.getVariables();
    const baseVariables: MemCommonEditorMentionChip[] = [
      {
        id: "insert-template-variable-user-name",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["user name", "user", "name"],
        groupTitle: SLASH_MENU_GROUP_TITLES.TemplateVariable,
        action: {
          kind: MemCommonEditorActionKind.InsertTemplateVariable,
          payload: {
            code: TemplateVariableCode.UserName,
            displayName: variables[TemplateVariableCode.UserName].displayName,
          },
        },
        menuItem: {
          id: "insert-template-variable-user-name",
          label: "User's Name",
          icon: MDSIconKind.Template,
          variant: MDSMenuItemVariant.OneLine,
        },
      },
      {
        id: "insert-template-variable-user-first-name",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["variable", "user's first name", "user", "first name"],
        groupTitle: SLASH_MENU_GROUP_TITLES.TemplateVariable,
        action: {
          kind: MemCommonEditorActionKind.InsertTemplateVariable,
          payload: {
            code: TemplateVariableCode.UserFirstName,
            displayName: variables[TemplateVariableCode.UserFirstName].displayName,
          },
        },
        menuItem: {
          id: "insert-template-variable-user-first-name",
          label: "User's First Name",
          icon: MDSIconKind.Template,
          variant: MDSMenuItemVariant.OneLine,
        },
      },
      {
        id: "insert-template-variable-date-today",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["variable", "date", "day", "today"],
        groupTitle: SLASH_MENU_GROUP_TITLES.TemplateVariable,
        action: {
          kind: MemCommonEditorActionKind.InsertTemplateVariable,
          payload: {
            code: TemplateVariableCode.DateToday,
            displayName: variables[TemplateVariableCode.DateToday].displayName,
          },
        },
        menuItem: {
          id: "insert-template-variable-date-today",
          label: "Today",
          icon: MDSIconKind.Template,
          variant: MDSMenuItemVariant.OneLine,
        },
      },
    ];
    if (!isCalendarEventTemplate) return baseVariables;

    const calendarEventTemplateVariables: MemCommonEditorMentionChip[] = [
      {
        id: "insert-template-variable-calendar-event-title",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["meeting title", "meeting", "calendar event", "title"],
        groupTitle: SLASH_MENU_GROUP_TITLES.CalendarEventTemplate,
        action: {
          kind: MemCommonEditorActionKind.InsertTemplateVariable,
          payload: {
            code: TemplateVariableCode.CalendarEventTitle,
            displayName: variables[TemplateVariableCode.CalendarEventTitle].displayName,
          },
        },
        menuItem: {
          id: "insert-template-variable-calendar-event-title",
          label: "Meeting Title",
          icon: MDSIconKind.CalendarEvent,
          variant: MDSMenuItemVariant.OneLine,
        },
      },
      {
        id: "insert-template-variable-calendar-event-date",
        kind: MemCommonMentionKind.SlashCommand,
        searchTerms: ["meeting date", "meeting", "calendar event", "date"],
        groupTitle: SLASH_MENU_GROUP_TITLES.CalendarEventTemplate,
        action: {
          kind: MemCommonEditorActionKind.InsertTemplateVariable,
          payload: {
            code: TemplateVariableCode.CalendarEventDate,
            displayName: variables[TemplateVariableCode.CalendarEventDate].displayName,
          },
        },
        menuItem: {
          id: "insert-template-variable-calendar-event-date",
          label: "Meeting Date",
          icon: MDSIconKind.CalendarEvent,
          variant: MDSMenuItemVariant.OneLine,
        },
      },
    ];

    return [...calendarEventTemplateVariables, ...baseVariables];
  };

  getTemplateSuggestionChips = async ({
    mentionQueryText,
  }: {
    mentionQueryText: string;
  }): Promise<MemCommonEditorMentionChip[]> => {
    const output: MemCommonEditorMentionChip[] = [];
    let templates: TemplateObservable[] = [];

    if (mentionQueryText) {
      // If there's a query, get suggestions using search
      const templateSuggestions = await this.search({
        mentionQueryText,
        suggestionType: SearchSuggestionType.TEMPLATE,
      });
      const templateIds = templateSuggestions.map(suggestion => suggestion.modelId);
      templates = await this.store.templates.getManyAsync(templateIds);
    } else {
      // If not, get most recent templates
      const recentTemplates = this.store.templates.sortedRecentTemplatesInteractedWithByMe.slice(
        0,
        SLASH_MENU_MAX_SEARCH_RESULTS_PER_GROUP
      );
      templates = recentTemplates.map(recentTemplate => recentTemplate.template);
    }

    for (const template of templates) {
      output.push({
        id: `insert-template-${template.id}`,
        kind: MemCommonMentionKind.SlashCommand,
        groupTitle: SLASH_MENU_GROUP_TITLES.Template,
        searchTerms: [template.title || UNTITLED_TEMPLATE_TITLE],
        action: () => this.insertTemplateAction(template.id),
        menuItem: {
          id: `insert-template-${template.id}`,
          label: template.title || UNTITLED_TEMPLATE_TITLE,
          icon: MDSIconKind.Template,
          variant: MDSMenuItemVariant.OneLine,
        },
      });
    }

    return output;
  };

  getNoteSuggestionChips = async ({
    mentionQueryText,
    excludeId,
    inlineCreation,
  }: {
    mentionQueryText: string;
    excludeId?: string;
    inlineCreation?: boolean;
  }) => {
    const output: MemCommonEditorMentionChip[] = [];

    // Find notes to generate chips for
    let notes: NoteObservable[] = [];
    if (mentionQueryText) {
      // If there's a query, get suggestions using search
      const noteSuggestions = await this.search({
        mentionQueryText,
        excludeId,
        suggestionType: SearchSuggestionType.NOTE,
      });
      const noteIds = noteSuggestions.map(suggestion => suggestion.modelId);
      notes = await this.store.notes.getManyAsync(noteIds);
    } else {
      // If not, get the most recent notes
      const noteIds = this.store.recentItems.sortedRecentNotesInteractedWithByMe
        .filter(note => note.id !== excludeId)
        .slice(0, NOTES_MAX_RESULTS)
        .map(note => note.id);
      notes = await this.store.notes.getManyAsync(noteIds);
    }

    // Iterate through the notes to generate the chips
    let exactMatchIndex = -1;
    let shouldShowCreateNewNote = inlineCreation && !!mentionQueryText.length;
    const groupTitle = mentionQueryText
      ? SLASH_MENU_GROUP_TITLES.Notes
      : SLASH_MENU_GROUP_TITLES.RecentNotes;

    for (let i = 0; i < notes.length; i++) {
      const note = notes[i];

      const title = note.title || UNTITLED_NOTE_TITLE;
      const subtitle = generateRecentDateString(
        DateTime.fromISO(
          note.lastMentionedAt ||
            note.lastViewedAt ||
            note.receivedAt ||
            note.locallyCreatedAt ||
            ""
        ),
        { skipFullDayName: true }
      );

      // Keep track of any potential exact matches
      if (title.toLowerCase().trim() === mentionQueryText.toLowerCase().trim()) {
        exactMatchIndex = i;

        // If there's an exact match that's owned by the user, don't show the create new note option
        if (note.isOwnedByMe) shouldShowCreateNewNote = false;
      }

      output.push({
        id: note.id,
        kind: MemCommonMentionKind.Note,
        searchTerms: [title],
        groupTitle,
        menuItem: {
          id: note.id,
          label: title,
          subtitle,
          icon: MDSIconKind.Document,
          variant: MDSMenuItemVariant.TwoLine,
        },
      });
    }

    // Move exact match to the top
    if (exactMatchIndex >= 0) {
      const exactMatch = output[exactMatchIndex];
      output.splice(exactMatchIndex, 1);
      output.unshift(exactMatch);
    }

    if (shouldShowCreateNewNote) {
      const newNoteId = uuidv4();

      output.push({
        id: newNoteId,
        kind: MemCommonMentionKind.Note,
        searchTerms: [mentionQueryText],
        groupTitle,
        beforeSelection: () =>
          this.createNoteBeforeSelection({ noteId: newNoteId, mentionQueryText }),
        menuItem: {
          id: newNoteId,
          label: mentionQueryText,
          subtitle: `Create new note`,
          icon: MDSIconKind.Plus,
          variant: MDSMenuItemVariant.TwoLine,
        },
      });
    }

    return output;
  };

  getCollectionSuggestionChips = async ({
    mentionQueryText,
    excludeId,
    inlineCreation,
  }: {
    mentionQueryText: string;
    excludeId?: string;
    inlineCreation?: boolean;
  }) => {
    const output: MemCommonEditorMentionChip[] = [];

    // Find collections to generate chips for
    let collections: CollectionObservable[] = [];
    if (mentionQueryText) {
      // If there's a query, get suggestions using search
      const collectionSuggestions = await this.search({
        mentionQueryText,
        excludeId,
        suggestionType: SearchSuggestionType.COLLECTION,
      });
      const collectionIds = collectionSuggestions.map(suggestion => suggestion.modelId);
      collections = await this.store.collections.getManyAsync(collectionIds);
    } else {
      // If not, get the most recent collections
      collections = this.store.recentItems.sortedRecentCollectionsInteractedWithByMe
        .filter(collection => collection.id !== excludeId)
        .slice(0, COLLECTIONS_MAX_RESULTS);
    }

    // Iterate through the collections to generate the chips
    let exactMatchIndex = -1;
    let shouldShowCreateNewCollection = inlineCreation && !!mentionQueryText.length;
    const groupTitle = mentionQueryText
      ? SLASH_MENU_GROUP_TITLES.Collections
      : SLASH_MENU_GROUP_TITLES.RecentCollections;

    for (let i = 0; i < collections.length; i++) {
      const collection = collections[i];

      const label = collection.label;
      const subtitle = await collection.getSubtitleAsync();

      // Keep track of any potential exact matches
      if (label.toLowerCase().trim() === mentionQueryText.toLowerCase().trim()) {
        exactMatchIndex = i;

        // If there's an exact match that's owned by the user, don't show the create new collection option
        if (collection.isOwnedByMe) shouldShowCreateNewCollection = false;
      }

      output.push({
        id: collection.id,
        kind: MemCommonMentionKind.Collection,
        searchTerms: [label],
        groupTitle,
        menuItem: {
          id: collection.id,
          label,
          subtitle,
          icon: ({ size, boxSize }) => (
            <MDSCollectionIcon size={size} boxSize={boxSize} collectionId={collection.id} />
          ),
          variant: MDSMenuItemVariant.TwoLine,
        },
      });
    }

    // Move exact match to the top
    if (exactMatchIndex >= 0) {
      const exactMatch = output[exactMatchIndex];
      output.splice(exactMatchIndex, 1);
      output.unshift(exactMatch);
    }

    // Optionally add the option to create a new collection
    if (shouldShowCreateNewCollection) {
      const newCollectionId = uuidv4();

      output.push({
        id: newCollectionId,
        kind: MemCommonMentionKind.Collection,
        searchTerms: [mentionQueryText],
        groupTitle,
        beforeSelection: () =>
          this.createCollectionBeforeSelection({ collectionId: newCollectionId, mentionQueryText }),
        menuItem: {
          id: newCollectionId,
          icon: MDSIconKind.Plus,
          label: mentionQueryText,
          subtitle: `Create new collection`,
          variant: MDSMenuItemVariant.TwoLine,
        },
      });
    }

    return output;
  };

  private async insertFileAction(
    mode: InsertFileMode
  ): Promise<Maybe<MemCommonEditorSlashCommand>> {
    const isImageMode = mode === InsertFileMode.Image;
    const file = await fileModule.askForUserFile(isImageMode ? "image/*" : undefined);
    if (!file) return;

    const fileId = uuidv4();
    const { info, variant } = await commonEditorFileModule.extractInfoAsync({ file, fileId });

    if (isImageMode) {
      if (variant !== MemCommonEditorFileVariant.Image) return;

      return {
        kind: MemCommonEditorActionKind.InsertImage,
        payload: {
          imageId: fileId,
          imageMimeType: info.fileMimeType,
          imageName: file.name,
          encodedImageContent: info.encodedFileContent,
        },
      };
    }

    return {
      kind: MemCommonEditorActionKind.InsertFile,
      payload: { fileName: file.name, ...info },
    };
  }

  private async insertTemplateAction(
    templateId: string
  ): Promise<Maybe<MemCommonEditorSlashCommand>> {
    const template = await this.store.templates.getAsync(templateId);

    const templateContents = await template?.getTemplateContentsForInsertionAsync();
    if (!template || !templateContents) return;

    const templateVariables = Object.entries(this.store.templates.getVariables()).map(
      ([code, variable]) => ({
        code,
        value: variable.value,
      })
    );

    const { remoteContent, documentUpdates } = templateContents;

    trackEvent(TrackedEvent.TemplateUsed, {
      template_name: template.title || UNTITLED_TEMPLATE_TITLE,
      context: TrackedEventContext.InsertMenu,
    });

    return {
      kind: MemCommonEditorActionKind.InsertTemplate,
      payload: {
        templateId,
        templateRemoteContent: remoteContent,
        templateDocumentUpdates: documentUpdates,
        templateVariables,
      },
    };
  }

  private async createNoteBeforeSelection({
    noteId,
    mentionQueryText,
  }: {
    noteId: string;
    mentionQueryText: string;
  }) {
    // Note: This throws `NoteCreateGateDeniedError` when the quota blocks creation.
    await this.store.notes.createNote({
      noteId,
      eventContext: EventContext.EditorInline,
      paywallContext: PaywallContext.NoteMentionCreate,
    });

    const encodedContent = notesModule.convertMdxToEncodedContent("# " + mentionQueryText);
    const queue = this.store.notes.getNoteQueue({ noteId });
    queue.push(
      new UpdateNoteContentUsingDiffOperation({
        store: this.store,
        payload: {
          id: noteId,
          encoded_content_diff: encodedContent || "",
        },
        primaryLabel: mentionQueryText,
        secondaryLabel: "",
        mediaKinds: [],
      })
    );
  }

  private async createCollectionBeforeSelection({
    collectionId,
    mentionQueryText,
  }: {
    collectionId: string;
    mentionQueryText: string;
  }) {
    await this.store.collections.createCollection({
      collectionId,
      title: mentionQueryText,
      description: "",
      eventContext: EventContext.EditorInline,
    });
  }
}
